import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { 
  Building2, 
  Palette, 
  Upload, 
  Save, 
  Eye, 
  Phone, 
  Mail, 
  MapPin,
  FileText,
  CheckCircle,
  AlertCircle,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { predefinedThemes, applyTheme, getTheme } from '../themes/predefinedThemes';
import Resizer from 'react-image-file-resizer';

const DepartmentSettings = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [department, setDepartment] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    // Persist tab across re-renders
    return sessionStorage.getItem('departmentSettingsTab') || 'general';
  });
  const [tabLocked, setTabLocked] = useState(false); // Prevent unwanted tab switching
  const [preventTabReset, setPreventTabReset] = useState(false);
  const [isUploading, setIsUploading] = useState(false); // Prevent reset during logo upload
  
  // Form states
  const [generalForm, setGeneralForm] = useState({
    name: '',
    department_email: '',
    department_phone: '',
    department_address: '',
    legal_text: ''
  });
  
  const [brandingForm, setBrandingForm] = useState({
    logo_url: '',
    primary_color: '#2563eb',
    secondary_color: '#1e40af', 
    accent_color: '#3b82f6',
    theme_name: 'default'
  });

  const [pricingForm, setPricingForm] = useState({
    cd_dvd_cost: 5.00,
    usb_cost: 15.00,
    print_cost_per_page: 0.25,
    certified_print_cost_per_page: 2.00,
    shipping_cost: 5.00,
    rush_processing_fee: 25.00,
    video_processing_fee: 10.00,
    audio_processing_fee: 5.00
  });
  
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [recentlyUploadedLogoUrl, setRecentlyUploadedLogoUrl] = useState(null); // Track recent uploads

  // Fetch department data
  useEffect(() => {
    fetchDepartmentData();
  }, []);

  // Handle component re-mount due to department updates
  useEffect(() => {
    const handleDepartmentUpdate = () => {
      // If we have a persisted tab and it's not general, restore it
      const persistedTab = sessionStorage.getItem('departmentSettingsTab');
      if (persistedTab && persistedTab !== 'general' && persistedTab !== activeTab) {
        console.log('Restoring persisted tab after department update:', persistedTab);
        setActiveTab(persistedTab);
      }
    };

    // Listen for department updates
    window.addEventListener('departmentUpdated', handleDepartmentUpdate);
    
    return () => {
      window.removeEventListener('departmentUpdated', handleDepartmentUpdate);
    };
  }, []);

  // Update preview when branding form changes
  useEffect(() => {
    // Apply current theme colors to preview
    if (brandingForm.primary_color && brandingForm.secondary_color && brandingForm.accent_color) {
      const currentTheme = getTheme(brandingForm.theme_name);
      const customTheme = {
        ...currentTheme,
        primary_color: brandingForm.primary_color,
        secondary_color: brandingForm.secondary_color,
        accent_color: brandingForm.accent_color
      };
      applyTheme(customTheme);
    }
  }, [brandingForm.primary_color, brandingForm.secondary_color, brandingForm.accent_color, brandingForm.theme_name]);

  const fetchDepartmentData = async () => {
    try {
      // Don't reset tab during logo upload process
      const currentTab = activeTab;
      const shouldPreventReset = preventTabReset;
      
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/departments/current`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setDepartment(response.data);
      setIsOwner(response.data.is_owner || false);
      
      // Populate forms but don't reset active tab during logo upload
      setGeneralForm({
        name: response.data.name || '',
        department_email: response.data.department_email || '',
        department_phone: response.data.department_phone || '',
        department_address: response.data.department_address || '', 
        legal_text: response.data.legal_text || ''
      });
      
      setBrandingForm({
        logo_url: response.data.logo_url || '',
        primary_color: response.data.primary_color || '#2563eb',
        secondary_color: response.data.secondary_color || '#1e40af',
        accent_color: response.data.accent_color || '#3b82f6',
        theme_name: response.data.theme_name || 'default'
      });

      setPricingForm({
        cd_dvd_cost: response.data.cd_dvd_cost || 5.00,
        usb_cost: response.data.usb_cost || 15.00,
        print_cost_per_page: response.data.print_cost_per_page || 0.25,
        certified_print_cost_per_page: response.data.certified_print_cost_per_page || 2.00,
        shipping_cost: response.data.shipping_cost || 5.00,
        rush_processing_fee: response.data.rush_processing_fee || 25.00,
        video_processing_fee: response.data.video_processing_fee || 10.00,
        audio_processing_fee: response.data.audio_processing_fee || 5.00
      });
      
      // Apply current theme but don't reset tab
      const currentTheme = getTheme(response.data.theme_name || 'default');
      applyTheme(currentTheme);
      
      // Restore tab if it was changed during logo upload
      if (shouldPreventReset && currentTab !== 'general') {
        console.log('Preserving tab during department update:', currentTab);
        setActiveTab(currentTab);
      }
      
      return response.data; // Return the fresh data
      
    } catch (error) {
      console.error('Error fetching department:', error);
      toast.error('Failed to load department settings');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleGeneralSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/departments/current`,
        generalForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('General settings updated successfully');
      await fetchDepartmentData(); // Refresh data
      
      // Force parent dashboard to refresh
      window.dispatchEvent(new CustomEvent('departmentUpdated', { 
        detail: { ...department, ...generalForm }
      }));
      
    } catch (error) {
      console.error('Error updating general settings:', error);
      toast.error('Failed to update general settings');
    } finally {
      setSaving(false);
    }
  };

  const handleBrandingSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    console.log('DEBUG: Branding submit started');
    console.log('DEBUG: Current branding form:', brandingForm);
    console.log('DEBUG: Current logoFile state:', logoFile?.name);
    console.log('DEBUG: Recently uploaded logo URL:', recentlyUploadedLogoUrl);
    
    // Get recently uploaded logo URL from sessionStorage if state is null (due to re-renders)
    const sessionLogoUrl = sessionStorage.getItem('recentlyUploadedLogoUrl');
    const effectiveRecentLogoUrl = recentlyUploadedLogoUrl || sessionLogoUrl;
    
    console.log('DEBUG: Session logo URL:', sessionLogoUrl);
    console.log('DEBUG: Effective recent logo URL:', effectiveRecentLogoUrl);
    
    // CRITICAL FIX: Use recently uploaded logo URL if available and different from current form
    let finalBrandingForm = { ...brandingForm };
    
    if (effectiveRecentLogoUrl && effectiveRecentLogoUrl !== brandingForm.logo_url) {
      console.log('DEBUG: Using recently uploaded logo URL instead of form URL');
      console.log('DEBUG: Form logo URL:', brandingForm.logo_url);
      console.log('DEBUG: Recent logo URL:', effectiveRecentLogoUrl);
      finalBrandingForm.logo_url = effectiveRecentLogoUrl;
    }
    
    console.log('DEBUG: Final branding form to submit:', finalBrandingForm);

    try {
      const brandingData = {
        logo_url: finalBrandingForm.logo_url,
        primary_color: finalBrandingForm.primary_color,
        secondary_color: finalBrandingForm.secondary_color,
        accent_color: finalBrandingForm.accent_color,
        theme_name: finalBrandingForm.theme_name
      };
      
      console.log('DEBUG: Sending branding data to backend:', brandingData);
      
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/departments/current`,
        brandingData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('DEBUG: Backend response:', response.data);
      console.log('DEBUG: Updated logo URL from backend:', response.data.logo_url);
      
      // Update local department state
      setDepartment(response.data);
      
      // Apply theme
      const selectedTheme = getTheme(brandingForm.theme_name);
      applyTheme({
        ...selectedTheme,
        ...brandingForm
      });
      
      toast.success('✅ Branding updated successfully', {
        duration: 2000
      });
      
      // Clear logo file and preview since it's now saved
      setLogoFile(null);
      setLogoPreview(null);
      setRecentlyUploadedLogoUrl(null); // Clear the recent upload URL
      sessionStorage.removeItem('recentlyUploadedLogoUrl'); // Clear from sessionStorage too
      
      // Allow tab resets again after successful save
      setPreventTabReset(false);
      
      // Clear the persisted tab after save to allow normal navigation
      setTimeout(() => {
        sessionStorage.removeItem('departmentSettingsTab');
      }, 2000);
      
      // Refresh data from backend to get the latest logo_url
      const refreshedDepartment = await fetchDepartmentData();
      
      // Force parent dashboard to refresh with the actual department data
      if (refreshedDepartment && !isUploading) {
        console.log('DEBUG: Dispatching department update event with:', refreshedDepartment);
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('departmentUpdated', {
            detail: refreshedDepartment
          }));
        }, 500);
      }
      
    } catch (error) {
      console.error('DEBUG: Error updating branding:', error);
      toast.error('Failed to update branding settings');
    } finally {
      setSaving(false);
    }
  };

  const handlePricingSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/departments/current`,
        pricingForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Pricing settings updated successfully');
      await fetchDepartmentData(); // Refresh data
      
      // Force parent dashboard to refresh
      window.dispatchEvent(new CustomEvent('departmentUpdated', { 
        detail: { ...department, ...pricingForm }
      }));
      
    } catch (error) {
      console.error('Error updating pricing settings:', error);
      toast.error('Failed to update pricing settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoChange = async (e) => {
    // Prevent any form submission or navigation
    e.preventDefault();
    e.stopPropagation();
    
    const file = e.target.files[0];
    if (!file) {
      console.log('DEBUG: No file selected');
      return;
    }
    
    console.log('DEBUG: Logo upload started - File:', file.name, 'Size:', file.size, 'Type:', file.type);
    
    // Prevent re-render loops during upload
    setIsUploading(true);
    
    // Force lock to branding tab and persist it
    const brandingTab = 'branding';
    setTabLocked(true);
    setPreventTabReset(true);
    setActiveTab(brandingTab);
    sessionStorage.setItem('departmentSettingsTab', brandingTab);
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file (PNG, JPG, GIF, SVG)');
      setTabLocked(false);
      setPreventTabReset(false);
      return;
    }
    
    try {
      // Create local preview immediately with original file
      console.log('DEBUG: Creating local preview...');
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
        console.log('DEBUG: Local logo preview created successfully');
      };
      reader.readAsDataURL(file);
      
      // Determine if we need to resize the image
      let fileToUpload = file;
      const maxSize = 2 * 1024 * 1024; // 2MB limit for optimization
      
      if (file.size > maxSize) {
        console.log('DEBUG: Image is large, resizing...', file.size, 'bytes');
        toast('Optimizing large image...', { duration: 2000 });
        
        try {
          // Resize the image
          fileToUpload = await resizeImage(file, 800, 600, 85);
          console.log('DEBUG: Image resized - New size:', fileToUpload.size, 'bytes');
          
          // Update preview with resized image
          const resizedReader = new FileReader();
          resizedReader.onload = (e) => {
            setLogoPreview(e.target.result);
            console.log('DEBUG: Resized logo preview updated');
          };
          resizedReader.readAsDataURL(fileToUpload);
          
          toast.success('Image optimized for faster loading');
        } catch (resizeError) {
          console.error('DEBUG: Error resizing image:', resizeError);
          toast.error('Failed to optimize image, uploading original');
          fileToUpload = file;
        }
      }
      
      // Final size check (after potential resizing)
      if (fileToUpload.size > 5 * 1024 * 1024) {
        toast.error('Image is still too large after optimization. Please use a smaller image.');
        setTabLocked(false);
        setPreventTabReset(false);
        return;
      }
      
      // Upload to backend
      console.log('DEBUG: Uploading to backend...');
      const formData = new FormData();
      formData.append('logo', fileToUpload);
      
      const token = localStorage.getItem('token');
      
      toast('Uploading logo...', { duration: 2000 });
      
      const uploadResponse = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/departments/upload-logo`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      
      const uploadedLogoUrl = uploadResponse.data.logo_url;
      console.log('DEBUG: Backend upload successful. Logo URL:', uploadedLogoUrl);
      
      // Store the recently uploaded logo URL in sessionStorage (survives re-renders)
      sessionStorage.setItem('recentlyUploadedLogoUrl', uploadedLogoUrl);
      setRecentlyUploadedLogoUrl(uploadedLogoUrl);
      
      // Update branding form with uploaded logo URL
      setBrandingForm(prev => {
        const updated = { ...prev, logo_url: uploadedLogoUrl };
        console.log('DEBUG: Updated branding form:', updated);
        return updated;
      });
      setLogoFile(fileToUpload);
      
      // FORCE stay on branding tab and persist it
      setActiveTab(brandingTab);
      sessionStorage.setItem('departmentSettingsTab', brandingTab);
      
      // Keep tab locked longer to prevent department update interference
      setTimeout(() => {
        setTabLocked(false);
        console.log('DEBUG: Tab unlocked after logo upload');
      }, 3000);
      
      // Show success message with file size info
      const sizeInfo = fileToUpload.size !== file.size ? 
        ` (optimized from ${(file.size / 1024 / 1024).toFixed(1)}MB to ${(fileToUpload.size / 1024 / 1024).toFixed(1)}MB)` : 
        '';
      
      console.log('DEBUG: Upload process completed successfully');
      toast.success(`✅ Logo uploaded successfully${sizeInfo}! Click "Apply Branding Changes" to save`);
      
    } catch (error) {
      console.error('DEBUG: Logo upload error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to upload logo';
      toast.error(errorMessage);
      setTabLocked(false);
      setPreventTabReset(false);
      
      // Clear any preview on error
      setLogoPreview(null);
    } finally {
      // Always clear uploading flag
      setIsUploading(false);
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleLogoDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        handleLogoChange({ target: { files: [file] } });
      } else {
        toast.error('Please drop an image file (PNG, JPG, GIF, SVG)');
      }
    }
  };

  // Vanilla JS file input setup - must be after handleLogoChange is defined
  useEffect(() => {
    const setupVanillaInput = () => {
      const container = document.getElementById('vanilla-file-area');
      if (container) {
        // Clear any existing content
        container.innerHTML = '';
        
        // Create invisible file input that overlays the React upload area
        const input = document.createElement('input');
        input.type = 'file';
        input.id = 'vanilla-file-input';
        input.accept = 'image/*';
        input.style.cssText = `
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          opacity: 0;
          cursor: pointer;
          z-index: 10;
        `;
        
        // Add event listener
        input.addEventListener('change', function(e) {
          if (e.target.files && e.target.files.length > 0) {
            // Call React's handleLogoChange with the vanilla JS event
            handleLogoChange(e);
          }
        });
        
        container.appendChild(input);
      }
    };
    
    // Setup with slight delay to ensure DOM is ready
    const timer = setTimeout(setupVanillaInput, 100);
    return () => clearTimeout(timer);
  }, [handleLogoChange]);

  const previewTheme = (themeName) => {
    const theme = getTheme(themeName);
    
    // Update the form with the new theme colors
    const newBrandingForm = {
      ...brandingForm,
      theme_name: themeName,
      primary_color: theme.primary_color,
      secondary_color: theme.secondary_color,
      accent_color: theme.accent_color
    };
    
    setBrandingForm(newBrandingForm);
    
    // Apply theme immediately for preview
    applyTheme(theme);
    
    // Show feedback to user
    toast.success(`Applied ${theme.name} theme`);
  };

  const previewColorChange = (colorType, color) => {
    // Update form immediately
    setBrandingForm(prev => ({ ...prev, [colorType]: color }));
    
    // Apply color change immediately
    const currentTheme = getTheme(brandingForm.theme_name);
    const updatedTheme = {
      ...currentTheme,
      ...brandingForm,
      [colorType]: color
    };
    applyTheme(updatedTheme);
  };

  // Image resizing utility function
  const resizeImage = (file, maxWidth = 800, maxHeight = 600, quality = 80) => {
    return new Promise((resolve) => {
      Resizer.imageFileResizer(
        file,
        maxWidth,
        maxHeight,
        'PNG',
        quality,
        0,
        (resizedImage) => {
          resolve(resizedImage);
        },
        'file'
      );
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="outline" 
            onClick={() => window.history.back()}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-slate-800">Department Settings</h1>
            <p className="text-slate-600 mt-1">Customize your department's branding and information</p>
          </div>
        </div>
        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
          <CheckCircle className="w-4 h-4 mr-1" />
          {department?.subscription_status || 'Active'}
          {isOwner && <span className="ml-2 text-xs">(Owner)</span>}
        </Badge>
      </div>

      <Tabs value={activeTab} onValueChange={(newTab) => {
        console.log('Tab change requested:', newTab, 'Current:', activeTab, 'Locked:', tabLocked);
        
        // If tab is locked (during logo upload), prevent switching to general
        if (tabLocked && newTab === 'general' && activeTab !== 'general') {
          console.log('Preventing unwanted tab switch to general during logo upload');
          return; // Don't change tab
        }
        
        // Persist tab selection
        sessionStorage.setItem('departmentSettingsTab', newTab);
        setActiveTab(newTab);
        console.log('Tab changed to:', newTab);
      }} className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="general" className="text-xs sm:text-sm">
            <Building2 className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">General</span>
            <span className="sm:hidden">Gen</span>
          </TabsTrigger>
          <TabsTrigger value="branding" className="text-xs sm:text-sm">
            <Palette className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Branding</span>
            <span className="sm:hidden">Brand</span>
          </TabsTrigger>
          <TabsTrigger value="pricing" className="text-xs sm:text-sm">
            <FileText className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Pricing</span>
            <span className="sm:hidden">Price</span>
          </TabsTrigger>
          <TabsTrigger value="preview" className="text-xs sm:text-sm">
            <Eye className="w-4 h-4 mr-1 sm:mr-2" />
            Preview
          </TabsTrigger>
        </TabsList>

        {/* General Settings Tab */}
        <TabsContent value="general" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>General Information</CardTitle>
              <CardDescription>
                Basic department information and contact details
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleGeneralSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Department Name</Label>
                    <Input
                      id="name"
                      value={generalForm.name}
                      onChange={(e) => setGeneralForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Shaker Heights Police Department"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email">Department Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="email"
                        type="email"
                        className="pl-10"
                        value={generalForm.department_email}
                        onChange={(e) => setGeneralForm(prev => ({ ...prev, department_email: e.target.value }))}
                        placeholder="contact@department.gov"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone">Department Phone</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="phone"
                        className="pl-10"
                        value={generalForm.department_phone}
                        onChange={(e) => setGeneralForm(prev => ({ ...prev, department_phone: e.target.value }))}
                        placeholder="(555) 123-4567"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="address">Department Address</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="address"
                        className="pl-10"
                        value={generalForm.department_address}
                        onChange={(e) => setGeneralForm(prev => ({ ...prev, department_address: e.target.value }))}
                        placeholder="123 Main St, City, State 12345"
                        required
                      />
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="legal">Legal Text</Label>
                  <div className="relative">
                    <FileText className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Textarea
                      id="legal"
                      className="pl-10 min-h-[100px]"
                      value={generalForm.legal_text}
                      onChange={(e) => setGeneralForm(prev => ({ ...prev, legal_text: e.target.value }))}
                      placeholder="Records released in accordance with applicable public records laws..."
                    />
                  </div>
                  <p className="text-sm text-gray-500">
                    This text will appear on request forms and documents
                  </p>
                </div>
                
                <Button type="submit" disabled={saving} className="w-full">
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save General Settings  
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Branding Tab */}
        <TabsContent value="branding" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Logo Upload */}
            <Card>
              <CardHeader>
                <CardTitle>Department Logo</CardTitle>
                <CardDescription>
                  Upload your department logo - Supported formats: PNG, JPG, JPEG, GIF, SVG (max 5MB)
                  <br />
                  <span className="text-blue-600 text-sm">✨ Large images will be automatically optimized for faster loading</span>
                  {logoPreview && <span className="text-green-600 ml-2">✓ Logo uploaded - ready to save!</span>}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-center w-full relative">
                  <label 
                    htmlFor="logo-upload" 
                    className={`flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors ${
                      isUploading ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    onDrop={handleLogoDrop}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                  >
                    <div className="flex flex-col items-center justify-center pt-5 pb-6 pointer-events-none">
                      {department?.logo_url && !logoPreview ? (
                        <div className="flex flex-col items-center space-y-2">
                          <img 
                            src={department.logo_url.startsWith('http') ? department.logo_url : `${process.env.REACT_APP_BACKEND_URL}${department.logo_url}`}
                            alt="Current Department Logo"
                            className="h-16 w-auto max-w-32 object-contain mb-2 pointer-events-none"
                          />
                          <p className="text-sm text-gray-600 font-medium">Current Logo</p>
                          <p className="text-xs text-gray-500">Click or drag to replace</p>
                        </div>
                      ) : logoPreview ? (
                        <div className="flex flex-col items-center space-y-2">
                          <img 
                            src={logoPreview}
                            alt="Logo Preview"
                            className="h-16 w-auto max-w-32 object-contain mb-2 pointer-events-none"
                          />
                          <p className="text-sm text-green-600 font-medium">New Logo Preview</p>
                          <p className="text-xs text-green-500">Ready to save!</p>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center space-y-2">
                          <svg className="w-8 h-8 mb-4 text-gray-500 pointer-events-none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"/>
                          </svg>
                          <p className="mb-2 text-sm text-gray-500 pointer-events-none">
                            <span className="font-semibold">Click to upload</span> or drag and drop
                          </p>
                          <p className="text-xs text-gray-500 pointer-events-none">PNG, JPG, GIF, SVG (max 5MB)</p>
                        </div>
                      )}
                      {isUploading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                            <span className="text-sm text-gray-600">Uploading...</span>
                          </div>
                        </div>
                      )}
                    </div>
                    <input 
                      id="logo-upload" 
                      type="file" 
                      className="hidden" 
                      accept="image/*"
                      onChange={handleLogoChange}
                      disabled={isUploading}
                    />
                    
                    {/* Vanilla JS file input overlay for reliable file selection */}
                    <div id="vanilla-file-area" style={{ 
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      pointerEvents: 'auto',
                      zIndex: 5
                    }}>
                    </div>
                  </label>
                </div>

                
                {logoPreview && (
                  <div className="text-center">
                    <div className="bg-green-50 border border-green-200 rounded p-2">
                      <p className="text-sm text-green-700">✅ Logo uploaded successfully!</p>
                      <p className="text-xs text-green-600">Switch to Preview tab to see it, then save changes.</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Color Customization */}
            <Card>
              <CardHeader>
                <CardTitle>Color Scheme</CardTitle>
                <CardDescription>Customize your department colors</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-8 h-8 rounded border-2 border-gray-300"
                      style={{ backgroundColor: brandingForm.primary_color }}
                    ></div>
                    <div className="flex-1">
                      <Label htmlFor="primary">Primary Color</Label>
                      <Input
                        id="primary"
                        type="color"
                        value={brandingForm.primary_color}
                        onChange={(e) => previewColorChange('primary_color', e.target.value)}
                        className="h-10"
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-8 h-8 rounded border-2 border-gray-300"
                      style={{ backgroundColor: brandingForm.secondary_color }}
                    ></div>
                    <div className="flex-1">
                      <Label htmlFor="secondary">Secondary Color</Label>
                      <Input
                        id="secondary"
                        type="color"
                        value={brandingForm.secondary_color}
                        onChange={(e) => previewColorChange('secondary_color', e.target.value)}
                        className="h-10"
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-8 h-8 rounded border-2 border-gray-300"
                      style={{ backgroundColor: brandingForm.accent_color }}
                    ></div>
                    <div className="flex-1">
                      <Label htmlFor="accent">Accent Color</Label>
                      <Input
                        id="accent"
                        type="color"
                        value={brandingForm.accent_color}
                        onChange={(e) => previewColorChange('accent_color', e.target.value)}
                        className="h-10"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Theme Selection */}
          <Card>
            <CardHeader>
              <CardTitle>Predefined Themes</CardTitle>
              <CardDescription>Choose from our professionally designed themes</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {Object.entries(predefinedThemes).map(([key, theme]) => (
                  <div 
                    key={key}
                    className={`p-3 border-2 rounded-lg cursor-pointer transition-all hover:shadow-md ${
                      brandingForm.theme_name === key ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                    }`}
                    onClick={() => previewTheme(key)}
                  >
                    <div className="flex gap-1 mb-2">
                      <div 
                        className="w-4 h-4 rounded-full border" 
                        style={{ backgroundColor: theme.primary_color }}
                      ></div>
                      <div 
                        className="w-4 h-4 rounded-full border"
                        style={{ backgroundColor: theme.secondary_color }}
                      ></div>
                      <div 
                        className="w-4 h-4 rounded-full border"
                        style={{ backgroundColor: theme.accent_color }}
                      ></div>
                    </div>
                    <h4 className="font-medium text-sm">{theme.name}</h4>
                    <p className="text-xs text-gray-600 mt-1">{theme.description}</p>
                    {brandingForm.theme_name === key && (
                      <div className="mt-2 text-xs text-blue-600 font-medium">✓ Selected</div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Save Branding */}
          <Card>
            <CardContent className="pt-6">
              <Button 
                onClick={handleBrandingSubmit} 
                disabled={saving} 
                className="w-full"
                size="lg"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Applying Changes...
                  </>
                ) : (
                  <>
                    <Palette className="w-4 h-4 mr-2" />
                    Apply Branding Changes
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pricing Tab */}
        <TabsContent value="pricing" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Pricing Configuration</CardTitle>
              <CardDescription>
                Set the costs for various services and media types
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePricingSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="cd_dvd_cost">CD/DVD Cost ($)</Label>
                    <Input
                      id="cd_dvd_cost"
                      type="number"
                      step="0.01"
                      min="0"
                      value={pricingForm.cd_dvd_cost}
                      onChange={(e) => setPricingForm(prev => ({ ...prev, cd_dvd_cost: parseFloat(e.target.value) || 0 }))}
                      placeholder="5.00"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="usb_cost">USB Drive Cost ($)</Label>
                    <Input
                      id="usb_cost"
                      type="number"
                      step="0.01"
                      min="0"
                      value={pricingForm.usb_cost}
                      onChange={(e) => setPricingForm(prev => ({ ...prev, usb_cost: parseFloat(e.target.value) || 0 }))}
                      placeholder="15.00"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="print_cost_per_page">Print Cost per Page ($)</Label>
                    <Input
                      id="print_cost_per_page"
                      type="number"
                      step="0.01"
                      min="0"
                      value={pricingForm.print_cost_per_page}
                      onChange={(e) => setPricingForm(prev => ({ ...prev, print_cost_per_page: parseFloat(e.target.value) || 0 }))}
                      placeholder="0.25"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="certified_print_cost_per_page">Certified Print Cost per Page ($)</Label>
                    <Input
                      id="certified_print_cost_per_page"
                      type="number"
                      step="0.01"
                      min="0"
                      value={pricingForm.certified_print_cost_per_page}
                      onChange={(e) => setPricingForm(prev => ({ ...prev, certified_print_cost_per_page: parseFloat(e.target.value) || 0 }))}
                      placeholder="2.00"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="shipping_cost">Shipping Cost ($)</Label>
                    <Input
                      id="shipping_cost"
                      type="number"
                      step="0.01"
                      min="0"
                      value={pricingForm.shipping_cost}
                      onChange={(e) => setPricingForm(prev => ({ ...prev, shipping_cost: parseFloat(e.target.value) || 0 }))}
                      placeholder="5.00"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="rush_processing_fee">Rush Processing Fee ($)</Label>
                    <Input
                      id="rush_processing_fee"
                      type="number"
                      step="0.01"
                      min="0"
                      value={pricingForm.rush_processing_fee}
                      onChange={(e) => setPricingForm(prev => ({ ...prev, rush_processing_fee: parseFloat(e.target.value) || 0 }))}
                      placeholder="25.00"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="video_processing_fee">Video Processing Fee ($)</Label>
                    <Input
                      id="video_processing_fee"
                      type="number"
                      step="0.01"
                      min="0"
                      value={pricingForm.video_processing_fee}
                      onChange={(e) => setPricingForm(prev => ({ ...prev, video_processing_fee: parseFloat(e.target.value) || 0 }))}
                      placeholder="10.00"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="audio_processing_fee">Audio Processing Fee ($)</Label>
                    <Input
                      id="audio_processing_fee"
                      type="number"
                      step="0.01"
                      min="0"
                      value={pricingForm.audio_processing_fee}
                      onChange={(e) => setPricingForm(prev => ({ ...prev, audio_processing_fee: parseFloat(e.target.value) || 0 }))}
                      placeholder="5.00"
                      required
                    />
                  </div>
                </div>
                
                <Button type="submit" disabled={saving} className="w-full">
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Pricing Settings  
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Preview Tab */}
        <TabsContent value="preview" className="space-y-6">
          <Alert>
            <Eye className="h-4 w-4" />
            <AlertDescription>
              This is how your department portal looks with the current settings. 
              Changes are applied immediately when you modify colors or themes.
            </AlertDescription>
          </Alert>
          
          {/* Live Header Preview */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Header Preview
                {department?.logo_url && (
                  <Badge className="bg-green-100 text-green-800">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Logo Active
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>See how your logo and department name appear in the navigation</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-white border rounded-lg p-4 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0">
                    {department?.logo_url ? (
                      <img 
                        src={department.logo_url.startsWith('http') ? 
                          department.logo_url : 
                          `${process.env.REACT_APP_BACKEND_URL}${department.logo_url}`
                        }
                        alt="Department Logo"
                        className="h-10 w-auto object-contain department-logo border rounded bg-white"
                        style={{ maxWidth: '120px' }}
                        onError={(e) => {
                          console.error('Department logo error:', e);
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : logoPreview ? (
                      <img 
                        src={logoPreview}
                        alt="Logo Preview"
                        className="h-10 w-auto object-contain department-logo border rounded bg-white"
                        style={{ maxWidth: '120px' }}
                        onError={(e) => {
                          console.error('Logo preview error:', e);
                        }}
                      />
                    ) : (
                      <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div>
                    <h1 className="text-xl font-semibold" style={{ color: brandingForm.primary_color }}>
                      {generalForm.name || department?.name || 'Your Department Name'}
                    </h1>
                    <p className="text-sm text-gray-600">Owner Dashboard</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Theme Preview */}
          <Card>
            <CardHeader>
              <CardTitle>Theme Preview</CardTitle>
              <CardDescription>Current theme and color scheme</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">Selected Theme</h3>
                  <div className="flex items-center gap-3 p-3 border rounded" style={{ borderColor: brandingForm.primary_color }}>
                    <div className="flex gap-1">
                      <div 
                        className="w-4 h-4 rounded-full border" 
                        style={{ backgroundColor: brandingForm.primary_color }}
                      ></div>
                      <div 
                        className="w-4 h-4 rounded-full border"
                        style={{ backgroundColor: brandingForm.secondary_color }}
                      ></div>
                      <div 
                        className="w-4 h-4 rounded-full border"
                        style={{ backgroundColor: brandingForm.accent_color }}
                      ></div>
                    </div>
                    <div>
                      <p className="font-medium">{predefinedThemes[brandingForm.theme_name]?.name}</p>
                      <p className="text-sm text-gray-600">{predefinedThemes[brandingForm.theme_name]?.description}</p>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Button Preview</h3>
                  <div className="flex gap-3">
                    <button 
                      className="px-4 py-2 rounded text-white font-medium"
                      style={{ backgroundColor: brandingForm.primary_color }}
                    >
                      Primary Button
                    </button>
                    <button 
                      className="px-4 py-2 rounded text-white font-medium"
                      style={{ backgroundColor: brandingForm.secondary_color }}
                    >
                      Secondary Button
                    </button>
                    <button 
                      className="px-4 py-2 rounded text-white font-medium"
                      style={{ backgroundColor: brandingForm.accent_color }}
                    >
                      Accent Button
                    </button>
                  </div>
                  <div className="mt-2 text-xs text-gray-500">
                    Colors: {brandingForm.primary_color}, {brandingForm.secondary_color}, {brandingForm.accent_color}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Current Department Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <h3 className="font-semibold mb-2">General Info</h3>
                  <div className="space-y-2 text-sm">
                    <p><strong>Name:</strong> {generalForm.name}</p>
                    <p><strong>Email:</strong> {generalForm.department_email}</p>
                    <p><strong>Phone:</strong> {generalForm.department_phone}</p>
                    <p><strong>Address:</strong> {generalForm.department_address}</p>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Branding</h3>
                  <div className="space-y-2 text-sm">
                    <p><strong>Theme:</strong> {predefinedThemes[brandingForm.theme_name]?.name}</p>
                    <div className="flex gap-2">
                      <div 
                        className="w-6 h-6 rounded border"
                        style={{ backgroundColor: brandingForm.primary_color }}
                        title="Primary Color"
                      ></div>
                      <div 
                        className="w-6 h-6 rounded border"
                        style={{ backgroundColor: brandingForm.secondary_color }}
                        title="Secondary Color"
                      ></div>
                      <div 
                        className="w-6 h-6 rounded border"
                        style={{ backgroundColor: brandingForm.accent_color }}
                        title="Accent Color"
                      ></div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Pricing</h3>
                  <div className="space-y-2 text-sm">
                    <p><strong>CD/DVD:</strong> ${pricingForm.cd_dvd_cost.toFixed(2)}</p>
                    <p><strong>USB Drive:</strong> ${pricingForm.usb_cost.toFixed(2)}</p>
                    <p><strong>Print/Page:</strong> ${pricingForm.print_cost_per_page.toFixed(2)}</p>
                    <p><strong>Certified Print/Page:</strong> ${pricingForm.certified_print_cost_per_page.toFixed(2)}</p>
                    <p><strong>Shipping:</strong> ${pricingForm.shipping_cost.toFixed(2)}</p>
                    <p><strong>Rush Processing:</strong> ${pricingForm.rush_processing_fee.toFixed(2)}</p>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Pricing</h3>
                  <div className="space-y-2 text-sm">
                    <p><strong>CD/DVD:</strong> ${pricingForm.cd_dvd_cost.toFixed(2)}</p>
                    <p><strong>USB Drive:</strong> ${pricingForm.usb_cost.toFixed(2)}</p>
                    <p><strong>Print (per page):</strong> ${pricingForm.print_cost_per_page.toFixed(2)}</p>
                    <p><strong>Certified Print:</strong> ${pricingForm.certified_print_cost_per_page.toFixed(2)}/page</p>
                    <p><strong>Shipping:</strong> ${pricingForm.shipping_cost.toFixed(2)}</p>
                    <p><strong>Rush Processing:</strong> ${pricingForm.rush_processing_fee.toFixed(2)}</p>
                  </div>
                </div>
              </div>
              
              {generalForm.legal_text && (
                <div>
                  <h3 className="font-semibold mb-2">Legal Text</h3>
                  <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                    {generalForm.legal_text}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DepartmentSettings;